type Side = "top" | "bottom" | "left" | "right";

interface PositionConfig {
  side: Side;
  sideOffset: number;
  triggerRect: DOMRect;
  contentWidth: number;
  contentHeight: number;
  padding: number;
  narrowBreakpoint?: number;
}

interface Position {
  top: number;
  left: number;
}

const calculatePreferredPosition = (
  side: Side,
  triggerRect: DOMRect,
  contentWidth: number,
  contentHeight: number,
  sideOffset: number
): Position => {
  switch (side) {
    case "top":
      return {
        top: triggerRect.top - contentHeight - sideOffset,
        left: triggerRect.left + triggerRect.width / 2 - contentWidth / 2
      };
    case "bottom":
      return {
        top: triggerRect.bottom + sideOffset,
        left: triggerRect.left + triggerRect.width / 2 - contentWidth / 2
      };
    case "left":
      return {
        top: triggerRect.top + triggerRect.height / 2 - contentHeight / 2,
        left: triggerRect.left - contentWidth - sideOffset
      };
    case "right":
      return {
        top: triggerRect.top + triggerRect.height / 2 - contentHeight / 2,
        left: triggerRect.right + sideOffset
      };
  }
};

const applyViewportFlip = (
  position: Position,
  config: PositionConfig
): Position => {
  const { side, sideOffset, triggerRect, contentWidth, contentHeight } = config;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const spaceAbove = triggerRect.top;
  const spaceBelow = viewportHeight - triggerRect.bottom;
  const spaceLeft = triggerRect.left;
  const spaceRight = viewportWidth - triggerRect.right;

  let { top, left } = position;

  if (
    side === "top" &&
    spaceAbove < contentHeight + sideOffset &&
    spaceBelow > spaceAbove
  ) {
    top = triggerRect.bottom + sideOffset;
  } else if (
    side === "bottom" &&
    spaceBelow < contentHeight + sideOffset &&
    spaceAbove > spaceBelow
  ) {
    top = triggerRect.top - contentHeight - sideOffset;
  } else if (
    side === "left" &&
    spaceLeft < contentWidth + sideOffset &&
    spaceRight > spaceLeft
  ) {
    left = triggerRect.right + sideOffset;
  } else if (
    side === "right" &&
    spaceRight < contentWidth + sideOffset &&
    spaceLeft > spaceRight
  ) {
    left = triggerRect.left - contentWidth - sideOffset;
  }

  return { top, left };
};

const clampToViewport = (
  position: Position,
  contentWidth: number,
  contentHeight: number,
  padding: number,
  narrowBreakpoint?: number
): Position => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  let { top, left } = position;

  if (left < padding) {
    left = padding;
  } else if (left + contentWidth > viewportWidth - padding) {
    left = viewportWidth - contentWidth - padding;
  }

  if (top < padding) {
    top = padding;
  } else if (top + contentHeight > viewportHeight - padding) {
    top = viewportHeight - contentHeight - padding;
  }

  if (narrowBreakpoint && viewportWidth < narrowBreakpoint) {
    left = Math.max(padding, (viewportWidth - contentWidth) / 2);
  }

  return { top, left };
};

export const calculatePosition = (config: PositionConfig): Position => {
  const {
    side,
    sideOffset,
    triggerRect,
    contentWidth,
    contentHeight,
    padding,
    narrowBreakpoint
  } = config;

  const preferred = calculatePreferredPosition(
    side,
    triggerRect,
    contentWidth,
    contentHeight,
    sideOffset
  );
  const flipped = applyViewportFlip(preferred, config);
  return clampToViewport(
    flipped,
    contentWidth,
    contentHeight,
    padding,
    narrowBreakpoint
  );
};
